import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  Autocomplete,
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';

import { TCreatePackagingPurchaseOrder } from 'src/redux/slices/packaging-purchase-order';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';

import { IPackagingItemListItem } from 'src/types/packaging-item';

interface IPackagingPurchaseOrderNewItemRow {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
  onItemSelected?: (itemId: string, display: { itemName: string; itemCode: string }) => void;
}

export default function PackagingPurchaseOrderNewItemRow({
  index,
  remove,
  isLast,
  onItemSelected,
}: IPackagingPurchaseOrderNewItemRow) {
  const [itemSearchQuery, setItemSearchQuery] = useState('');

  const [localItems, setLocalItems] = useState<IPackagingItemListItem[]>([]);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<TCreatePackagingPurchaseOrder>();

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  const watchItems = useWatch({
    control,
    name: 'items',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedPackagingItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `packaging/item?page=&limit=&generalSearch=${query}&itemName=&itemCode=&minQuantity=&maxQuantity=&unit=&alertEnabled=&status=active&sortBy=itemName&sort=asc`
        );

        setLocalItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedPackagingItems(itemSearchQuery);

    return fetchPaginatedPackagingItems.cancel;
  }, [itemSearchQuery, fetchPaginatedPackagingItems]);

  return (
    <>
      <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', pb: 0.5 } }}>
        <TableCell>
          {index + 1 < 10 ? `0${index + 1}` : index + 1}

          <FormHelperText>{defaultHelperText}</FormHelperText>
        </TableCell>

        <TableCell sx={{ minWidth: 280 }}>
          <Controller
            name={`items.${index}.packagingItem`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                fullWidth
                size="small"
                multiple={false}
                options={localItems}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === 'string') {
                    return option === value;
                  }

                  if (option && '_id' in option) {
                    return option._id === value;
                  }

                  return false;
                }}
                getOptionLabel={(option: IPackagingItemListItem | string) => {
                  if (!option) {
                    return '';
                  }

                  if (typeof option === 'string') {
                    if (option.length === 0) {
                      return option;
                    }

                    const value = localItems.find(
                      (packagingItem: IPackagingItemListItem) => packagingItem._id === option
                    );
                    return value ? value.itemName : '';
                  }

                  if (option && 'itemName' in option) {
                    return option.itemName;
                  }

                  return '';
                }}
                renderOption={(props, option) => {
                  if (typeof option === 'string') {
                    return (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    );
                  }

                  return (
                    <li {...props} key={option._id}>
                      {option.itemName} ({option.itemCode})
                    </li>
                  );
                }}
                onInputChange={(_, value) => {
                  setItemSearchQuery(value);
                }}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    required
                    helperText={error ? error.message : defaultHelperText}
                    {...params}
                  />
                )}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    field.onChange(newValue);
                  } else {
                    field.onChange(newValue?._id);

                    if (newValue) {
                      onItemSelected?.(newValue._id, {
                        itemName: newValue.itemName,
                        itemCode: newValue.itemCode,
                      });
                    }
                  }
                }}
              />
            )}
          />
        </TableCell>

        <TableCell>
          <RHFTextField
            fullWidth
            required
            type="number"
            size="small"
            name={`items.${index}.quantity`}
            InputProps={{
              endAdornment: errors?.items?.[index]?.quantity ? (
                <InputAdornment position="end">
                  <Tooltip
                    title={errors?.items?.[index]?.quantity?.message || 'Invalid quantity'}
                  >
                    <Iconify icon="eva:info-outline" color="error.main" />
                  </Tooltip>
                </InputAdornment>
              ) : null,
            }}
            helperText={defaultHelperText}
            showError={false}
          />
        </TableCell>
      </TableRow>

      <TableRow sx={{ bgcolor: 'background.neutral' }}>
        <TableCell colSpan={3} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <RHFTextField
              size="small"
              type="number"
              name={`items.${index}.unitPrice`}
              label="Unit Cost"
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>Rs.</Box>
                  </InputAdornment>
                ),
                endAdornment: errors?.items?.[index]?.unitPrice ? (
                  <InputAdornment position="end">
                    <Tooltip title={errors?.items?.[index]?.unitPrice?.message || 'Invalid cost'}>
                      <Iconify icon="eva:info-outline" color="error.main" />
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              helperText={defaultHelperText}
              showError={false}
              sx={{ minWidth: 150 }}
            />

            <RHFTextField
              size="small"
              type="number"
              name={`items.${index}.discount`}
              label="Discount"
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>Rs.</Box>
                  </InputAdornment>
                ),
                endAdornment: errors?.items?.[index]?.discount ? (
                  <InputAdornment position="end">
                    <Tooltip
                      title={errors?.items?.[index]?.discount?.message || 'Invalid discount'}
                    >
                      <Iconify icon="eva:info-outline" color="error.main" />
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              helperText={defaultHelperText}
              showError={false}
              sx={{ minWidth: 150 }}
            />

            <RHFTextField
              size="small"
              type="number"
              name={`items.${index}.totalAfterDiscount`}
              label="Total"
              placeholder="0.00"
              onChange={(event) => {
                setValue(
                  `items.${index}.unitPrice`,
                  Number(event.target.value) / (watchItems[index]?.quantity || 1)
                );
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>Rs.</Box>
                  </InputAdornment>
                ),
              }}
              helperText={defaultHelperText}
              showError={false}
              sx={{ minWidth: 150 }}
            />

            <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
              <span>
                <IconButton
                  disabled={isLast}
                  color="error"
                  onClick={() => remove(index)}
                  sx={{ mt: 0.5 }}
                >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
}
